import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  checkDuplicatePhoneCount,
  fetchCanceledOrders,
  updateOrderStatus,
  deleteOrder,
  fetchCanceledOrdersSummary,
} from "../../api/OrderAPIs";
import { fetchDesignById } from "../../api/DesignAPIs";
import { fetchTypeById } from "../../api/TypeAPIs";
import { useAuth } from "../../utils/auth";
import { canViewOrders, canMutateOrders } from "../../utils/roles";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../../api/URL_Header";
import {
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  Package,
  User,
  Phone,
  MapPin,
  Map,
  DollarSign,
  Hash,
  Copy,
  Download,
  Link as LinkIcon,
  Edit,
  Trash2,
  Archive,
  Clock,
  AlertTriangle,
  ShoppingCart,
  Truck,
  CopyCheck,
  Filter,
  Save,
  Plus,
  Minus,
  Search,
  Loader,
  TrendingUp,
  CheckCircle,
  MessageSquare,
  Calendar,
  ArrowUpDown,
} from "lucide-react";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  } else if (type === "card") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  } else if (type === "productsCard") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }
  return null;
};

const isValidSecondPhone = (phone) =>
  phone && phone !== "غير معروف" && phone !== "Unknown Phone";

const CanceledOrderCard = ({
  order,
  authState,
  onCopyPhone,
  onDownload,
  onCopyExcel,
  onUpdateStatus,
  onDeleteOrder,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const canAct = canMutateOrders(authState.role);

  return (
    <div className="bg-white rounded-2xl border border-red-100 shadow-md overflow-hidden">
      <div className="p-4">
       <div
          className={`rounded-xl border p-4 mb-4 ${
            order.note ? "bg-amber-50 border-amber-200" : "bg-gray-50 border-gray-200"
          }`}
        >
          <div className="flex items-start gap-2">
            <MessageSquare size={18} className={order.note ? "text-amber-600" : "text-gray-400"} />
            <p
              style={{ fontFamily: "cairo black" }}
              className={`text-base leading-relaxed whitespace-pre-wrap ${
                order.note ? "text-amber-900" : "text-gray-400 italic"
              }`}
            >
              {order.note || "لا توجد ملاحظات"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono font-bold text-red-700 text-lg flex-shrink-0">#{order.orderNumber}</span>

          <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
            <User size={16} className="text-gray-500 flex-shrink-0" />
            <span style={{ fontFamily: "cairo black" }} className="text-lg text-gray-900 font-semibold truncate">
              {order.name}
            </span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => onCopyPhone(order.phoneNumber)}
              className="flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5 text-green-700 hover:bg-green-100 transition"
              title="اضغط لنسخ الرقم"
            >
              <Phone size={14} className="flex-shrink-0" />
              <span style={{ fontFamily: "monospace" }} className="font-bold text-base">
                {order.phoneNumber}
              </span>
            </button>
            {isValidSecondPhone(order.phoneNumber2) && (
              <button
                type="button"
                onClick={() => onCopyPhone(order.phoneNumber2)}
                className="flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5 text-green-700 hover:bg-green-100 transition"
                title="اضغط لنسخ الرقم"
              >
                <Phone size={14} className="flex-shrink-0" />
                <span style={{ fontFamily: "monospace" }} className="font-bold text-base">
                  {order.phoneNumber2}
                </span>
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => setIsExpanded((prev) => !prev)}
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-600 transition flex-shrink-0 mr-auto sm:mr-0"
            title={isExpanded ? "إخفاء التفاصيل" : "عرض التفاصيل"}
          >
            {isExpanded ? <ChevronUp size={22} /> : <ChevronDown size={22} />}
          </button>
        </div>

        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
            <div className="bg-gray-50 rounded-xl border border-gray-100 p-4 space-y-3">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-gray-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-xs text-gray-400 block mb-0.5">العنوان</span>
                  <span style={{ fontFamily: "cairo black" }} className="text-base text-gray-800 leading-relaxed block">
                    {order.address}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Map size={18} className="text-gray-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-xs text-gray-400 block mb-0.5">المحافظة</span>
                  <span className="text-xl font-bold text-purple-600">{order.state}</span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <DollarSign size={18} className="text-gray-500 flex-shrink-0" />
                <div>
                  <span className="text-xs text-gray-400 block mb-0.5">الإجمالي</span>
                  <span className="font-bold text-xl text-green-600">{order.total} ج.م</span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Calendar size={18} className="text-gray-500 flex-shrink-0" />
                <div>
                  <span className="text-xs text-gray-400 block mb-0.5">التاريخ</span>
                  <span className="text-gray-700">{order.date}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-500">تفاصيل الطلب</h3>
              <div className="bg-indigo-50 rounded-xl border border-indigo-100 p-4 space-y-3">
                {order.items.map((item, index) => (
                  <div key={`${item.id}-${index}`} className="bg-white rounded-lg border border-indigo-100 p-3">
                    <div className="font-medium text-indigo-800 text-base">{item.name}</div>
                    <div style={{ fontFamily: "cairo black" }} className="text-sm text-gray-700 mt-1">
                      النوع: {item.type}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <span className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">{item.code}</span>
                      {item.quantityIndex && (
                        <span className="text-xs text-gray-500">
                          {item.quantityIndex}/{item.totalQuantity}
                        </span>
                      )}
                      {item.driveLink && (
                        <>
                          <a
                            href={item.driveLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:text-indigo-800"
                            title="فتح الرابط"
                          >
                            <LinkIcon size={16} />
                          </a>
                          {item.code.startsWith("CUSTOM") && (
                            <button
                              type="button"
                              onClick={() => onDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)}
                              className="text-green-600 hover:text-green-800"
                              title="تحميل الصورة"
                            >
                              <Download size={16} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-500">ملاحظات</h3>
              <div
                className={`rounded-xl border p-4 ${
                  order.note ? "bg-amber-50 border-amber-200" : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="flex items-start gap-2">
                  <MessageSquare size={18} className={order.note ? "text-amber-600" : "text-gray-400"} />
                  <p
                    style={{ fontFamily: "cairo black" }}
                    className={`text-base leading-relaxed whitespace-pre-wrap ${
                      order.note ? "text-amber-900" : "text-gray-400 italic"
                    }`}
                  >
                    {order.note || "لا توجد ملاحظات"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-gray-100 px-4 py-4 space-y-3 bg-gray-50/50">
        {authState.role === "Admin" && (
          <button
            type="button"
            onClick={() => onCopyExcel(order)}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 active:scale-95 transition"
          >
            <Copy size={16} />
            نسخ Excel
          </button>
        )}

        {canAct ? (
        <>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <button
            type="button"
            onClick={() => onUpdateStatus(order.orderNumber, "Confirmed")}
            className="bg-green-600 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
          >
            <CheckCircle size={14} />
            مؤكد
          </button>
          <button
            type="button"
            onClick={() => onUpdateStatus(order.orderNumber, "Old")}
            className="bg-gray-600 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
          >
            <Archive size={14} />
            قديم
          </button>
          <button
            type="button"
            onClick={() => onUpdateStatus(order.orderNumber, "Hold")}
            className="bg-yellow-500 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
          >
            <Clock size={14} />
            معلق
          </button>
          <button
            type="button"
            onClick={() => onUpdateStatus(order.orderNumber, "Changes")}
            className="bg-purple-500 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
          >
            <Edit size={14} />
            تغييرات
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <button
            type="button"
            onClick={() => onUpdateStatus(order.orderNumber, "Under Purchase")}
            className="bg-indigo-500 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
          >
            <ShoppingCart size={13} />
            تحت الشراء
          </button>
          <button
            type="button"
            onClick={() => onUpdateStatus(order.orderNumber, "Deposit Required")}
            className="bg-orange-500 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
          >
            <DollarSign size={13} />
            ديبوزت
          </button>
          <button
            type="button"
            onClick={() => onDeleteOrder(order.orderNumber)}
            className="bg-red-500 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
          >
            <Trash2 size={13} />
            حذف
          </button>
          <button
            type="button"
            onClick={() => onUpdateStatus(order.orderNumber, "Spam")}
            className="bg-red-600 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
          >
            <AlertTriangle size={13} />
            Spam
          </button>
        </div>
        </>
        ) : (
          <p className="text-center text-sm text-slate-500 bg-white rounded-xl py-2 border border-slate-200">عرض فقط</p>
        )}
      </div>
    </div>
  );
};

const CanceledOrdersPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canAct = canMutateOrders(authState.role);
  const [loadedOrders, setLoadedOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [currentReference, setCurrentReference] = useState("Girabatcom.16000");
  const [referenceInput, setReferenceInput] = useState("Girabatcom.16000");
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [sortOrder, setSortOrder] = useState("desc");

  const { data: canceledOrdersSummary, isLoading: isLoadingSummary, error: errorSummary } = useQuery({
    queryKey: ["canceledOrdersSummary"],
    queryFn: fetchCanceledOrdersSummary,
    enabled: authState.role === "Admin",
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0 },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!canViewOrders(authState.role)) {
      navigate("/login");
      return;
    }
    loadInitialOrders();
  }, [authState, navigate, sortOrder]);

  useEffect(() => {
    const saved = localStorage.getItem("orderReference");
    if (saved) {
      setCurrentReference(saved);
      setReferenceInput(saved);
    }
  }, []);

  const isLoading = isLoadingSummary;
  const queryError = errorSummary;

  const handleDownload = async (url, filename = "design.jpg") => {
    try {
      const response = await fetch(
        `${API_BASE_URL_V2}/api/files/download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("فشل التحميل. تحقق من السيرفر.");
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (!successful) {
        toast.error("فشل النسخ، حاول مرة أخرى.");
      }
    } catch (err) {
      toast.error("فشل النسخ، حاول النسخ يدويًا.");
      console.error("Copy failed:", err);
    }

    document.body.removeChild(textArea);
  };

  const enrichOrders = async (data) => {
    return await Promise.all(
      data.map(async (order) => {
        let enrichedItems = [];

        for (const detail of order.orderDetails) {
          const design = await fetchDesignById(detail.designId);
          const type = await fetchTypeById(detail.typeId);
          if (!design || !type) continue;

          const baseItem = {
            id: detail.designId,
            name: design.Name,
            price: detail.price,
            type: type.name,
            driveLink: design.code.startsWith("CUSTOM") ? `${API_BASE_URL_V2}${design.driveLink}` : design.driveLink || "",
            code: design.code || "None",
          };

          for (let i = 0; i < detail.quantity; i++) {
            enrichedItems.push({
              ...baseItem,
              quantityIndex: i + 1,
              totalQuantity: detail.quantity,
            });
          }
        }

        return {
          orderNumber: order.orderId,
          items: enrichedItems,
          total: order.totalAmount,
          profit: order.profit || 0,
          date: new Date(order.date).toISOString().split("T")[0],
          address: order.address?.fullAddress || "Unknown Address",
          state: order.address?.state || "Unknown Address",
          phoneNumber: order.phoneNumber || "Unknown Phone",
          phoneNumber2: order.phoneNumber2 || "Unknown Phone",
          name: order.name || "Unknown Customer",
          note: order.note || "",
          status: order.status || "Canceled",
        };
      })
    );
  };

  const loadInitialOrders = async () => {
    setIsOrdersLoading(true);
    try {
      const data = await fetchCanceledOrders(0, pageSize, sortOrder);
      if (data.length === 0 || data.length < pageSize) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error loading initial orders:", err);
      setError("فشل في تحميل الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    setIsLoadingMore(true);
    try {
      const data = await fetchCanceledOrders(currentPage, pageSize, sortOrder);
      if (data.length === 0 || data.length < pageSize) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders((prev) => [...prev, ...enrichedOrders]);
      setCurrentPage((prev) => prev + 1);
    } catch (err) {
      console.error("Error loading more orders:", err);
      setError("فشل في تحميل المزيد من الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل المزيد من الطلبات!");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handlePageSizeChange = async (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
    setIsOrdersLoading(true);
    try {
      const data = await fetchCanceledOrders(0, newSize, sortOrder);
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
      setHasMore(data.length === newSize);
    } catch (err) {
      console.error("Error changing page size:", err);
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    if (!canAct) return;
    let reason = "no reason (normal order)";

    if (newStatus === "Old") {
      try {
        const confirmUpdate = window.confirm("هل تريد تحديث الطلب إلى قديم؟");
        if (!confirmUpdate) return;

        await updateOrderStatus(orderId, newStatus, reason);
        setLoadedOrders((prev) => prev.filter((o) => o.orderNumber !== orderId));
        toast.success("تم تحديث الطلب إلى قديم!");
      } catch (err) {
        console.error(err);
        toast.error("فشل في العملية: " + (err.message || "خطأ غير معروف"));
      }
      return;
    }

    if (newStatus === "Hold") {
      reason = window.prompt("سبب وضع الطلب على الانتظار:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    } else if (newStatus === "Changes") {
      reason = window.prompt("سبب التغيير:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    } else if (newStatus === "Spam") {
      reason = window.prompt("سبب التغيير:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    }

    const confirm = window.confirm(`تأكيد تحديث الحالة إلى "${newStatus}"؟`);
    if (!confirm) return;

    try {
      await updateOrderStatus(orderId, newStatus, reason);
      setLoadedOrders((prev) => prev.filter((o) => o.orderNumber !== orderId));
      toast.success(`تم تحديث الطلب إلى ${newStatus} بنجاح`);
    } catch (err) {
      toast.error("فشل التحديث: " + err.message);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!canAct) return;
    if (!canMutateOrders(authState.role)) return;
    const confirmDelete = window.confirm(`هل أنت متأكد من حذف الطلب رقم ${orderId}؟`);
    if (!confirmDelete) return;
    try {
      await deleteOrder(orderId);
      setLoadedOrders((prev) => prev.filter((order) => order.orderNumber !== orderId));
      toast.success(`تم حذف الطلب رقم ${orderId} بنجاح!`);
    } catch (err) {
      console.error("Error deleting order:", err);
      toast.error("فشل في حذف الطلب. يرجى المحاولة مرة أخرى.");
    }
  };

  const handleCopyPhone = (phone) => {
    if (!phone || phone === "Unknown Phone" || phone === "غير معروف") return;
    copyToClipboard(phone);
    toast.success("تم نسخ الرقم!");
  };

  const handleCopyBostaMessage = () => {
    const message = `
بنحب نبلغ حضرتك إن شركة الشحن هتتواصل مع حضرتك على الواتساب خلال وقت قصير لتأكيد العنوان واستلام الطلب ✅
تنويه مهم:
في رسالة التأكيد اللي هتوصلك ممكن تلاقي اسم شركة مختلفة (مثل TeePlus) 💼  

كمان عدد القطع ممكن يظهر 1 حتى لو كانت اكتر من قطعة واحدة 🧾
يُرجى الرد على رسالة شركة الشحن علشان يتم تأكيد الطلب ويتم بدء الشحن ✉️⚡  

شكرًا لاختيارك جرابتكم 🌷`;
    copyToClipboard(message);
  };

  const handleCopyAddressClarification = () => {
    const message = `أهلاً بحضرتك

العنوان المسجل غير واضح أو ناقص تفاصيل مهمة لتسليم الطلب بنجاح

برجاء توضيح العنوان بالتفصيل:
• اسم الشارع
• رقم العمارة / الشقة
• معالم قريبة (مثل: بجوار صيدلية، أمام مدرسة، إلخ)

شكرًا لتفهمك، هنبدأ تجهيز الطلب فور توضيح العنوان`;

    copyToClipboard(message);
  };

  const handleCopyAllExcelData = async (order) => {
    try {
      const count = await checkDuplicatePhoneCount(
        order.orderNumber,
        order.phoneNumber,
        order.phoneNumber2 || ""
      );

      if (count > 0) {
        const proceed = window.confirm(
          `تحذير: هذا العميل لديه ${count} طلب(ات) سابقة بنفس الرقم!\n\nهل تريد الاستمرار في النسخ؟`
        );
        if (!proceed) {
          toast.info("تم إلغاء النسخ بسبب تكرار الرقم");
          return;
        }
      }

      const phone = `${order.phoneNumber}${
        order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" ? "-" + order.phoneNumber2 : ""
      }`;
      const formattedState = `${order.state} (D)`;
      const customerBase = `${currentReference}\t\t${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;

      const items = order.items;
      const lines = [];

      items.forEach((item, index) => {
        const itemNum = `${index + 1}-${items.length}`;
        const type = item.type || "Unknown Type";
        const driveLink = item.driveLink || "No Link";

        const itemData = `\t\tPhone Cover (H)\t${type}\t\t1\t${driveLink}`;
        const itemLine = `${itemNum}${itemData}`;

        if (index === 0) {
          lines.push(`${customerBase}\t\t${itemLine}`);
        } else {
          lines.push(`${currentReference}\t\t\t\t\t\t\t\t\t\t\t${itemLine}`);
        }
      });

      const finalText = lines.join("\n");
      copyToClipboard(finalText);

      toast.success("تم نسخ الطلب كامل مع التكرار بنجاح!");
    } catch (err) {
      console.error("خطأ في النسخ:", err);
      toast.error("فشل في النسخ، حاول مرة تانية");
    }
  };

  const isOrderNonCustom = (order) => {
    return !order.items.some((item) => item.code && item.code.startsWith("CUSTOM"));
  };

  const handleCopyAllVisibleOrders = async () => {
    const nonCustomOrders = loadedOrders.filter(isOrderNonCustom);

    if (nonCustomOrders.length === 0) {
      toast.warn("لا توجد طلبات غير مخصصة (CUSTOM) للنسخ");
      return;
    }

    const confirm = window.confirm(
      `هل تريد نسخ ${nonCustomOrders.length} طلب(ات) غير مخصصة مرة واحدة إلى الإكسيل؟\n\n(الطلبات التي تحتوي على تصميم CUSTOM سيتم تخطيها)\n\nسيتم فحص التكرار + زيادة الرقم المرجع تلقائيًا`
    );
    if (!confirm) return;

    let finalLines = [];
    let hasDuplicate = false;
    let currentRef = currentReference;

    for (const order of nonCustomOrders) {
      try {
        const count = await checkDuplicatePhoneCount(
          order.orderNumber,
          order.phoneNumber,
          order.phoneNumber2 || ""
        );

        if (count > 0) {
          hasDuplicate = true;
          finalLines.push(`# تحذير: تكرار رقم (${count} طلب سابق) - ${order.name} - ${order.phoneNumber}`);
        }

        const phone = `${order.phoneNumber}${
          order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" ? "-" + order.phoneNumber2 : ""
        }`;
        const formattedState = `${order.state} (D)`;
        const customerBase = `${currentRef}\t\t${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;

        const items = order.items;
        const orderLines = [];

        items.forEach((item, index) => {
          const itemNum = `${index + 1}-${items.length}`;
          const type = item.type || "Unknown Type";
          const driveLink = item.driveLink || "No Link";

          const itemData = `\t\tPhone Cover (H)\t${type}\t\t1\t${driveLink}`;
          const itemLine = `${itemNum}${itemData}`;

          if (index === 0) {
            orderLines.push(`${customerBase}\t\t${itemLine}`);
          } else {
            orderLines.push(`${currentRef}\t\t\t\t\t\t\t\t\t\t\t${itemLine}`);
          }
        });

        finalLines.push(...orderLines);

        const match = currentRef.match(/(\d+)$/);
        if (match) {
          const num = parseInt(match[0], 10);
          currentRef = currentRef.replace(/\d+$/, (num + 1).toString());
        }
      } catch (err) {
        finalLines.push(`# خطأ في الطلب رقم ${order.orderNumber}`);
      }
    }

    setCurrentReference(currentRef);
    setReferenceInput(currentRef);
    localStorage.setItem("orderReference", currentRef);

    const finalText = finalLines.join("\n");
    copyToClipboard(finalText);

    toast.success(
      `تم نسخ ${nonCustomOrders.length} طلب(ات) غير مخصصة بنجاح!${hasDuplicate ? " (يوجد تكرار في بعض الأرقام)" : ""}`,
      { autoClose: 6000 }
    );
  };

  const handleMarkAllAsOld = async () => {
    const nonCustomOrders = loadedOrders.filter(isOrderNonCustom);

    if (nonCustomOrders.length === 0) {
      toast.warn("لا توجد طلبات غير مخصصة (CUSTOM) لتحويلها إلى قديم");
      return;
    }

    const confirm = window.confirm(
      `هل تريد تحويل ${nonCustomOrders.length} طلب(ات) غير مخصصة إلى "قديم"؟\n\n` +
        `(الطلبات التي تحتوي على تصميم CUSTOM سيتم تخطيها)\n\n` +
        `الطلبات المحددة ستختفي من الصفحة فورًا.`
    );

    if (!confirm) return;

    let successCount = 0;
    let failedCount = 0;

    toast.info("جاري تحديث الطلبات غير المخصصة إلى قديم... لا تغلق الصفحة", {
      autoClose: false,
      toastId: "marking-old",
    });

    for (const order of nonCustomOrders) {
      try {
        await updateOrderStatus(order.orderNumber, "Old", "تم التحديث الجماعي بدون نسخ (غير مخصص)");
        successCount++;
      } catch (err) {
        console.error(`فشل تحديث الطلب ${order.orderNumber}:`, err);
        failedCount++;
      }
    }

    setLoadedOrders((prev) => prev.filter((order) => !nonCustomOrders.some((o) => o.orderNumber === order.orderNumber)));

    toast.dismiss("marking-old");

    toast.success(
      `تم تحويل ${successCount} طلب(ات) غير مخصصة إلى قديم بنجاح!` +
        (failedCount > 0 ? `\nفشل في ${failedCount} طلب(ات)` : ""),
      { autoClose: 6000 }
    );
  };

  const handleMarkAllAsConfirmed = async () => {
    if (loadedOrders.length === 0) {
      toast.warn("لا توجد طلبات ملغاة للتحويل");
      return;
    }

    const confirm = window.confirm(
      `هل تريد تحويل ${loadedOrders.length} طلب(ات) ملغاة إلى "مؤكد"؟\n\n` +
        `الطلبات ستختفي من هذه الصفحة فورًا.`
    );

    if (!confirm) return;

    let successCount = 0;
    let failedCount = 0;

    toast.info("جاري تحويل الطلبات الملغاة إلى مؤكد... لا تغلق الصفحة", {
      autoClose: false,
      toastId: "marking-confirmed",
    });

    for (const order of loadedOrders) {
      try {
        await updateOrderStatus(order.orderNumber, "Confirmed", "تم التحديث الجماعي من ملغي إلى مؤكد");
        successCount++;
      } catch (err) {
        console.error(`فشل تحديث الطلب ${order.orderNumber}:`, err);
        failedCount++;
      }
    }

    setLoadedOrders((prev) =>
      prev.filter((order) => !loadedOrders.some((o) => o.orderNumber === order.orderNumber))
    );

    toast.dismiss("marking-confirmed");

    toast.success(
      `تم تحويل ${successCount} طلب(ات) إلى مؤكد بنجاح!` +
        (failedCount > 0 ? `\nفشل في ${failedCount} طلب(ات)` : ""),
      { autoClose: 6000 }
    );
  };

  const handleMarkAllAsSpam = async () => {
    if (loadedOrders.length === 0) {
      toast.warn("لا توجد طلبات ملغاة للتحويل");
      return;
    }

    const spamReason = "canceled order";
    const confirm = window.confirm(
      `هل تريد تحويل ${loadedOrders.length} طلب(ات) إلى "Spam"؟\n\n` +
        `سيتم تسجيل السبب: "${spamReason}"\n\n` +
        `الطلبات ستختفي من هذه الصفحة فورًا.`
    );

    if (!confirm) return;

    let successCount = 0;
    let failedCount = 0;

    toast.info('جاري تحويل الطلبات إلى Spam... لا تغلق الصفحة', {
      autoClose: false,
      toastId: "marking-spam",
    });

    for (const order of loadedOrders) {
      try {
        await updateOrderStatus(order.orderNumber, "Spam", spamReason);
        successCount++;
      } catch (err) {
        console.error(`فشل تحديث الطلب ${order.orderNumber}:`, err);
        failedCount++;
      }
    }

    setLoadedOrders((prev) =>
      prev.filter((order) => !loadedOrders.some((o) => o.orderNumber === order.orderNumber))
    );

    toast.dismiss("marking-spam");

    toast.success(
      `تم تحويل ${successCount} طلب(ات) إلى Spam بنجاح!` +
        (failedCount > 0 ? `\nفشل في ${failedCount} طلب(ات)` : ""),
      { autoClose: 6000 }
    );
  };

  const filteredOrders = loadedOrders.filter((order) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      order.orderNumber.toString().includes(term) ||
      order.name.toLowerCase().includes(term) ||
      order.phoneNumber.includes(term) ||
      order.phoneNumber2?.includes(term)
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Package className="text-indigo-600" size={32} />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              الطلبات الملغاة
            </h1>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200"
          >
            <ChevronRight size={20} />
            العودة للرئيسية
          </button>
        </div>

        {(error || queryError) && (
          <div className="bg-red-100 text-red-700 p-4 rounded-xl mb-6 border border-red-200">
            {error || "فشل في تحميل بيانات الطلبات الملغاة. يرجى المحاولة مرة أخرى."}
          </div>
        )}

        {/* Stats Cards */}
        {authState.role === "Admin" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 hover:shadow-2xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-indigo-100 rounded-xl">
                  <Package className="text-indigo-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-gray-800">ملخص الطلبات الملغاة</h2>
              </div>
              {isLoading ? (
                <SkeletonLoader type="card" />
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-indigo-50 rounded-xl">
                    <span className="text-gray-600">عدد الطلبات</span>
                    <span className="text-2xl font-bold text-indigo-600">{canceledOrdersSummary.count}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl">
                    <span className="text-gray-600">إجمالي المبلغ</span>
                    <span className="text-2xl font-bold text-green-600">{canceledOrdersSummary.totalAmount} ج.م</span>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 hover:shadow-2xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <TrendingUp className="text-purple-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-gray-800">إجمالي الأرباح</h2>
              </div>
              {isLoading ? (
                <SkeletonLoader type="productsCard" />
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-xl">
                    <span className="text-gray-600">الربح الإجمالي</span>
                    <span className="text-2xl font-bold text-purple-600">{canceledOrdersSummary.totalProfit} ج.م</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quick Message Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-amber-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <MapPin className="text-amber-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-amber-900 text-lg">طلب توضيح العنوان</h3>
                  <p className="text-sm text-amber-700">إذا كان العنوان غير واضح</p>
                </div>
              </div>
              <button
                onClick={handleCopyAddressClarification}
                className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              >
                <Copy size={18} />
                نسخ الرسالة
              </button>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Truck className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-blue-900 text-lg">رسالة بوسطة</h3>
                  <p className="text-sm text-blue-700">لشركة الشحن</p>
                </div>
              </div>
              <button
                onClick={handleCopyBostaMessage}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              >
                <Copy size={18} />
                نسخ الرسالة
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border-2 border-green-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-xl">
                  <CopyCheck className="text-green-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-green-900 text-xl">نسخ جماعي للإكسيل</h3>
                  <p className="text-sm text-green-700">نسخ {loadedOrders.length} طلب مع فحص التكرار</p>
                </div>
              </div>
              <button
                onClick={handleCopyAllVisibleOrders}
                disabled={isOrdersLoading || loadedOrders.length === 0}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Copy size={20} />
                نسخ الكل
              </button>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border-2 border-purple-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <Archive className="text-purple-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-purple-900 text-xl">تحويل الكل إلى قديم</h3>
                  <p className="text-sm text-purple-700">تنظيف سريع لـ {loadedOrders.length} طلب</p>
                </div>
              </div>
              <button
                onClick={handleMarkAllAsOld}
                disabled={isOrdersLoading || loadedOrders.length === 0}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-bold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Archive size={20} />
                تحويل للقديم
              </button>
            </div>
          </div>

          <div className="bg-gradient-to-r from-emerald-50 to-green-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border-2 border-emerald-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-emerald-100 rounded-xl">
                  <CheckCircle className="text-emerald-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-emerald-900 text-xl">تحويل الكل إلى مؤكد</h3>
                  <p className="text-sm text-emerald-700">تحويل {loadedOrders.length} طلب ملغي إلى مؤكد</p>
                </div>
              </div>
              <button
                onClick={handleMarkAllAsConfirmed}
                disabled={isOrdersLoading || loadedOrders.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white font-bold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <CheckCircle size={20} />
                تحويل للمؤكد
              </button>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-50 to-rose-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border-2 border-red-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-red-100 rounded-xl">
                  <AlertTriangle className="text-red-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-red-900 text-xl">تحويل الكل إلى Spam</h3>
                  <p className="text-sm text-red-700">
                    تحويل {loadedOrders.length} طلب مع سبب: canceled order
                  </p>
                </div>
              </div>
              <button
                onClick={handleMarkAllAsSpam}
                disabled={isOrdersLoading || loadedOrders.length === 0}
                className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-bold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <AlertTriangle size={20} />
                Spam الكل
              </button>
            </div>
          </div>
        </div>

        {/* Reference Section */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-8 border border-white/30">
          <div className="flex items-center gap-3 mb-4">
            <Hash className="text-indigo-600" size={24} />
            <h2 className="text-xl font-bold text-gray-800">إعداد رقم المرجع (Reference)</h2>
          </div>
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-right">الرقم الحالي</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const match = currentReference.match(/(\d+)$/);
                    if (match) {
                      const num = parseInt(match[0], 10);
                      const newNum = num + 1;
                      const newRef = currentReference.replace(/\d+$/, newNum.toString());
                      setCurrentReference(newRef);
                      setReferenceInput(newRef);
                      localStorage.setItem("orderReference", newRef);
                    }
                  }}
                  className="bg-green-500 hover:bg-green-600 text-white w-12 h-12 rounded-r-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                  title="زيادة الرقم"
                >
                  <Plus size={20} />
                </button>
                <div className="flex-1 text-3xl font-bold text-indigo-600 bg-indigo-50 py-3 px-4 text-center border-2 border-indigo-200">
                  {currentReference}
                </div>
                <button
                  onClick={() => {
                    const match = currentReference.match(/(\d+)$/);
                    if (match) {
                      const num = parseInt(match[0], 10);
                      const newNum = num - 1;
                      if (newNum >= 0) {
                        const newRef = currentReference.replace(/\d+$/, newNum.toString());
                        setCurrentReference(newRef);
                        setReferenceInput(newRef);
                        localStorage.setItem("orderReference", newRef);
                      }
                    }
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white w-12 h-12 rounded-l-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                  title="إنقاص الرقم"
                >
                  <Minus size={20} />
                </button>
              </div>
            </div>
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-right">
                ابدأ من (مثال: Girabatcom.150)
              </label>
              <input
                type="text"
                value={referenceInput}
                onChange={(e) => setReferenceInput(e.target.value)}
                onBlur={() => {
                  if (referenceInput.trim() && referenceInput.includes(".")) {
                    setCurrentReference(referenceInput.trim());
                    localStorage.setItem("orderReference", referenceInput.trim());
                    toast.success("تم تحديث رقم المرجع!");
                  }
                }}
                className="w-full p-4 text-lg border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-400 transition-all"
                placeholder="Girabatcom.4000"
                style={{ fontFamily: "cairo black !important" }}
              />
            </div>
            <button
              onClick={() => {
                if (referenceInput.trim()) {
                  setCurrentReference(referenceInput.trim());
                  setReferenceInput(referenceInput.trim());
                  localStorage.setItem("orderReference", referenceInput.trim());
                  toast.success("تم تحديث رقم المرجع!");
                }
              }}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold flex items-center gap-2"
            >
              <Save size={20} />
              تحديث الرقم
            </button>
            <button
              onClick={handleCopyAllVisibleOrders}
              disabled={isOrdersLoading || loadedOrders.length === 0}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold px-5 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Copy size={20} />
              نسخ الكل ({loadedOrders.length})
            </button>
            <button
              onClick={handleMarkAllAsOld}
              disabled={isOrdersLoading || loadedOrders.length === 0}
              className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-bold px-5 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Archive size={20} />
              تحويل للقديم ({loadedOrders.length})
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-3 text-right">
            كل ما تنسخ بيانات عميل بعد "تحديث إلى قديم"، الرقم سيزيد تلقائيًا
          </p>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="بحث برقم الطلب أو اسم العميل أو رقم الهاتف..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-400 transition-all"
              />
            </div>
            <button
              type="button"
              onClick={() => setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"))}
              className={`flex items-center gap-2 px-6 py-3 border-2 rounded-xl transition-all ${
                sortOrder === "desc"
                  ? "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100"
                  : "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"
              }`}
              title={sortOrder === "desc" ? "الأحدث أولاً" : "الأقدم أولاً"}
            >
              <ArrowUpDown size={20} />
              {sortOrder === "desc" ? "الأحدث أولاً" : "الأقدم أولاً"}
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
            >
              <Filter size={20} />
              تصفية
            </button>
          </div>
        </div>

        {/* Page Size Selector */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <label className="font-medium text-gray-700">عرض:</label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-400 transition-all bg-white"
                disabled={isOrdersLoading}
              >
                <option value={10}>10 طلبات</option>
                <option value={20}>20 طلب</option>
                <option value={30}>30 طلب</option>
                <option value={40}>40 طلب</option>
                <option value={50}>50 طلب</option>
                <option value={80}>80 طلب</option>
                <option value={100}>100 طلب</option>
              </select>
              <span className="text-2xl text-gray-600">(إجمالي المعروض: {loadedOrders.length})</span>
            </div>
            {isOrdersLoading && (
              <div className="flex items-center gap-2 text-indigo-600">
                <Loader className="animate-spin" size={20} />
                <span>جاري التحميل...</span>
              </div>
            )}
          </div>
        </div>

        {/* Orders List */}
        <div className="space-y-5">
          {isOrdersLoading ? (
            <div className="space-y-5">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
                  <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-24 bg-gray-200 rounded"></div>
                  <div className="h-20 bg-gray-200 rounded"></div>
                  <div className="h-16 bg-gray-200 rounded"></div>
                  <div className="h-12 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {filteredOrders.length > 0 ? (
                <>
                  {filteredOrders.map((order) => (
                    <CanceledOrderCard
                      key={order.orderNumber}
                      order={order}
                      authState={authState}
                      onCopyPhone={handleCopyPhone}
                      onDownload={handleDownload}
                      onCopyExcel={handleCopyAllExcelData}
                      onUpdateStatus={handleUpdateStatus}
                      onDeleteOrder={handleDeleteOrder}
                    />
                  ))}

                  {hasMore && (
                    <div className="text-center mt-8">
                      <button
                        onClick={loadMoreOrders}
                        disabled={isLoadingMore}
                        className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                      >
                        {isLoadingMore ? (
                          <>
                            <Loader className="animate-spin" size={20} />
                            جاري التحميل...
                          </>
                        ) : (
                          <>
                            <ChevronLeft size={20} />
                            تحميل المزيد
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <Package className="mx-auto mb-4 text-gray-400" size={64} />
                  <p className="text-2xl text-gray-700 mb-2">لا توجد طلبات ملغاة</p>
                  <p className="text-lg text-gray-500">سيتم عرض الطلبات الملغاة هنا</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="fixed bottom-6 right-6">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-4 rounded-full shadow-2xl hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center"
            title="العودة إلى الأعلى"
          >
            <ChevronUp size={24} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CanceledOrdersPage;
